import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWA } from '../../hooks/usePWA';
import { useOnboardingGuide } from '../../hooks/useOnboardingGuide';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../config/appStore';

const PWAInstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const { 
    showInstallPrompt, 
    isIOS, 
    isAndroid, 
    installPWA, 
    dismissPrompt, 
    getIOSInstructions,
    isInstalled
  } = usePWA();
  const { completeStep } = useOnboardingGuide();
  
  const [isInstalling, setIsInstalling] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Complete onboarding step if PWA is installed
  useEffect(() => {
    const isPWAInstalled = localStorage.getItem('pwa_installed') === 'true';
    if (isInstalled || isPWAInstalled) {
      completeStep('install-pwa');
    }
  }, [isInstalled, completeStep]);

  // En móvil ya existe app nativa en las stores: manda AppDownloadBanner y la
  // instalación de la PWA queda solo para escritorio, para no apilar dos avisos.
  const hasStoreApp = (isAndroid && Boolean(PLAY_STORE_URL)) || (isIOS && Boolean(APP_STORE_URL));

  if (!showInstallPrompt || hasStoreApp) {
    return null;
  }

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    setIsInstalling(true);
    try {
      const success = await installPWA();
      if (success) {
        // Complete onboarding step on successful installation
        completeStep('install-pwa');
      } else {
        // Handle installation failure
      }
    } catch (error) {
      console.error('Installation error:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
  };

  const handleNotNow = () => {
    dismissPrompt();
  };

  if (showIOSInstructions) {
    return (
      <div className="fixed top-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] animate-in slide-in-from-right duration-300">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 backdrop-blur-sm">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Instalar en iPhone
              </h3>
            </div>
            
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400 mb-3">
            {getIOSInstructions().map((instruction, index) => (
              <div key={index} className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-4 h-4 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-medium">
                  {index + 1}
                </span>
                <span className="leading-tight">{instruction}</span>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              Entendido
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              No mostrar más
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] animate-in slide-in-from-right duration-300">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-xl backdrop-blur-sm">
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold leading-tight">
                  Instalar GuideMe
                </h3>
                <p className="text-xs text-white/80 leading-tight">
                  Acceso rápido y notificaciones
                </p>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="text-white/60 hover:text-white/80 p-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex-1 bg-white text-blue-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-lg text-xs font-medium transition-colors"
            >
              {isInstalling ? (
                <div className="flex items-center justify-center space-x-1">
                  <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Instalando...</span>
                </div>
              ) : (
                <>
                  {isIOS ? 'Instrucciones' : 'Instalar Ahora'}
                </>
              )}
            </button>
            
            <button
              onClick={handleNotNow}
              className="text-white/80 hover:text-white text-xs font-medium px-2 py-2 rounded-lg hover:bg-white/10"
            >
              Después
            </button>
          </div>
          
          {isAndroid && (
            <div className="mt-3 flex items-center space-x-2 text-xs text-white/70 pt-2 border-t border-white/20">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span className="leading-tight">Funciona sin conexión</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;